import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PageContainer from "../../../components/pageContainer";
import dayjs from "dayjs";
import useUser from "../../../hooks/useUser";
import { UserResponse, UserRole, AccountStatus, UserUpdate } from "../../../client";
import PhoneNumberField from "../../../components/phoneNumberField";
import PasswordChangeDialog from "../../../components/passwordChangeDialog";

interface FormErrors {
  [key: string]: string | undefined;
  login_id?: string;
  name?: string;
  password?: string;
  email?: string;
  phone?: string;
  role?: string;
}

const AccountDetail: FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<UserResponse | null>(null);
  const { id } = useParams<{ id: string }>();
  const { getUser, updateUser, updatePassword } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<UserResponse | null>(null);

  const fetchData = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await getUser({ userId: id });
      if (response) {
        setData(response);
        setOriginalData(response);
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const validateForm = (): boolean => {
    if (!data) return false;
    const errors: FormErrors = {};
    let isValid = true;

    // ログインID のバリデーション
    if (!data.login_id?.trim()) {
      errors.login_id = 'ログインIDを入力してください';
      isValid = false;
    } else if (data.login_id.length < 4) {
      errors.login_id = 'ログインIDは4文字以上で入力してください';
      isValid = false;
    }

    // 氏名のバリデーション
    if (!data.name?.trim()) {
      errors.name = '氏名を入力してください';
      isValid = false;
    }

    // メールアドレスのバリデーション
    if (data.role === 'admin' && !data.email) {
      errors.email = '管理者の場合、メールアドレスは必須です';
      isValid = false;
    } else if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = '有効なメールアドレスを入力してください';
      isValid = false;
    }

    // 電話番号のバリデーション（入力されている場合のみ）
    if (data.phone && !/^[0-9]+$/.test(data.phone)) {
      errors.phone = '有効な電話番号を入力してください';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handlePasswordEdit = () => {
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordSave = async (newPassword: string) => {
    if (!id) return;

    try {
      await updatePassword({
        userId: id,
        requestBody: { password: newPassword },
      });
      setSuccessMessage("パスワードが正常に変更されました");
    } catch (error) {
      console.error("パスワード変更に失敗しました:", error);
    } finally {
      setIsPasswordDialogOpen(false);
    }
  };

  const handleSave = async () => {
    if (!data || !id) return;

    if (!validateForm()) {
      return;
    }

    setError(null);
    setFormErrors({});

    const updateData: UserUpdate = {
      name: data.name,
      login_id: data.login_id,
      email: data.email,
      phone: data.phone,
      role: data.role,
      status: data.status,
    };

    try {
      await updateUser({
        userId: id,
        requestBody: updateData,
      });
      setSuccessMessage("ユーザー情報を更新しました");
      setIsEditing(false);
      await fetchData();
    } catch (err: any) {
      console.log('Submit error:', err);

      if (err.type === 'DuplicateLoginIdError') {
        setFormErrors(prev => ({
          ...prev,
          login_id: err.message || 'このログインIDは既に使用されています'
        }));
      } else if (err.type === 'DuplicateEmailError') {
        setFormErrors(prev => ({
          ...prev,
          email: err.message || 'このメールアドレスは既に使用されています'
        }));
      } else if (err.type === 'ValidationError') {
        if (err.details) {
          const newFormErrors: FormErrors = {};
          Object.entries(err.details).forEach(([field, message]) => {
            if (typeof message === 'string') {
              newFormErrors[field] = message;
            }
          });
          setFormErrors(newFormErrors);
        } else {
          setError(err.message || '入力内容を確認してください');
        }
      } else {
        setError(err.message || '予期せぬエラーが発生しました');
      }
    }
  };

  const handleCancel = () => {
    setData(originalData); // 編集前のデータを復元
    setFormErrors({});
    setError(null);
    setIsEditing(false);
  };


  const handleEdit = () => {
    setOriginalData(data);
    setIsEditing(true);
  };

  const getRoleLabel = (role: UserRole | undefined) => {
    switch (role) {
      case "admin":
        return "管理者";
      case "internal_worker":
        return "社内作業者";
      case "external_worker":
        return "委託先作業者";
      default:
        return "未設定";
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case "admin":
        return "success";
      case "internal_worker":
        return "warning";
      case "external_worker":
        return "primary";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: AccountStatus | undefined) => {
    switch (status) {
      case "active":
        return "有効";
      case "inactive":
        return "無効";
      default:
        return "未設定";
    }
  };

  const getStatusColor = (status: AccountStatus) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "default";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }


  return (
    <PageContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="div">
          アカウント詳細
        </Typography>
        {data && (
          isEditing ? (
            <Box display="flex" gap={2}>
              <Button variant="outlined" onClick={handleCancel}>
                キャンセル
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                保存
              </Button>
            </Box>
          ) : (
            <Box display="flex" gap={2}>
              <Button variant="outlined" color="primary" onClick={handlePasswordEdit}>
                パスワード変更
              </Button>
              <Button variant="contained" color="primary" onClick={handleEdit}>
                編集
              </Button>
            </Box>
          )
        )}
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1} p={2}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ width: 200 }}>
                ユーザー名
              </TableCell>
              <TableCell>
                {isEditing && data ? (
                  <TextField
                    value={data.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                    fullWidth
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                  />
                ) : (
                  data?.name || "-"
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                ログインID
              </TableCell>
              <TableCell>
                {isEditing && data ? (
                  <TextField
                    value={data.login_id}
                    onChange={(e) => setData({ ...data, login_id: e.target.value })}
                    fullWidth
                    error={!!formErrors.login_id}
                    helperText={formErrors.login_id || "※ ログインIDは4文字以上で入力してください"}
                    required
                  />
                ) : (
                  data?.login_id || "-"
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                メールアドレス
              </TableCell>
              <TableCell>
                {isEditing && data ? (
                  <TextField
                    value={data.email || ""}
                    onChange={(e) => setData({ ...data, email: e.target.value })}
                    fullWidth
                    error={!!formErrors.email}
                    helperText={formErrors.email || (data.role === 'admin' ? "※ 管理者の場合、メールアドレスの入力は必須です" : undefined)}
                    type="email"
                  />
                ) : (
                  data?.email || "-"
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                電話番号
              </TableCell>
              <TableCell>
                {data ? (
                  <PhoneNumberField
                    value={data.phone || ""}
                    onChange={(value) => setData({ ...data, phone: value })}
                    isEditing={isEditing}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                  />
                ) : "-"}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell component="th" scope="row">
                役割
              </TableCell>
              <TableCell>
                {isEditing && data ? (
                  <FormControl fullWidth>
                    <Select
                      value={data.role ?? ""}
                      onChange={(e) => setData({ ...data, role: e.target.value as UserRole })}
                    >
                      <MenuItem value="admin">管理者</MenuItem>
                      <MenuItem value="internal_worker">社内作業者</MenuItem>
                      <MenuItem value="external_worker">委託先作業者</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  data?.role ? (
                    <Chip
                      label={getRoleLabel(data.role)}
                      color={getRoleColor(data.role)}
                    />
                  ) : "-"
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                ステータス
              </TableCell>
              <TableCell>
                {isEditing && data ? (
                  <FormControl fullWidth>
                    <Select
                      value={data.status ?? ""}
                      onChange={(e) => setData({ ...data, status: e.target.value as AccountStatus })}
                    >
                      <MenuItem value="active">有効</MenuItem>
                      <MenuItem value="inactive">無効</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  data?.status ? (
                    <Chip
                      label={getStatusLabel(data.status)}
                      color={getStatusColor(data.status)}
                    />
                  ) : "-"
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                作成日
              </TableCell>
              <TableCell>
                {data?.created_at ? dayjs(data.created_at).format("YYYY/M/D") : "-"}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                更新日
              </TableCell>
              <TableCell>
                {data?.updated_at ? dayjs(data.updated_at).format("YYYY/M/D") : "-"}
              </TableCell>
            </TableRow>
          </TableBody>

        </Table>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
          >
            {error}
          </Alert>
        )}
      </Box>

      {/* パスワード変更ダイアログ */}
      <PasswordChangeDialog
        open={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
        onSave={handlePasswordSave}
      />

      {/* 成功メッセージ用の Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AccountDetail;